import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Check, Bell } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const COPY = {
  flights:  { title: "Flights are launching soon", tagline: "Compare and negotiate on flights across India — coming in the SwaanStay app." },
  trains:   { title: "Trains are launching soon",  tagline: "IRCTC-integrated bookings with AI-picked seats — coming in the SwaanStay app." },
  buses:    { title: "Buses are launching soon",   tagline: "Sleepers, seaters, AC & non-AC across every state — coming in the SwaanStay app." },
  cabs:     { title: "Cabs are launching soon",    tagline: "Outstation and airport transfers with pre-negotiated fares — coming in the SwaanStay app." },
  holidays: { title: "Holidays are launching soon", tagline: "Curated multi-city packages with hotels, flights and experiences — coming in the SwaanStay app." },
};

/**
 * ComingSoonModal — capture waitlist signups for verticals we haven't shipped yet.
 * Uses shadcn Dialog. Posts to `/api/waitlist/notify` and shows a friendly
 * success state with the running waitlist count for that vertical.
 */
export default function ComingSoonModal({ vertical, open, onOpenChange, source = "landing" }) {
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(false);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!open) return;
    setContact(""); setOk(false);
    api.get("/waitlist/counts").then(({ data }) => setCounts(data?.counts || {})).catch(() => {});
  }, [open]);

  if (!vertical) return null;
  const copy = COPY[vertical] || { title: "Coming Soon", tagline: "This vertical is launching soon." };

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.trim()) return;
    setSending(true);
    try {
      await api.post("/waitlist/notify", { vertical, contact: contact.trim(), source });
      setOk(true);
      toast.success(`We'll notify you when ${vertical} goes live!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your interest. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const running = (counts[vertical] || 0) + (ok ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none" data-testid="coming-soon-modal">
        <div className="ss-surface ss-hero-gradient ss-noise relative rounded-2xl overflow-hidden">
          <div className="relative p-8 text-white">
            <DialogHeader>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C8A34D]/40 bg-[#C8A34D]/10 w-fit mb-4">
                <span className="ss-glow-dot" />
                <span className="ss-label text-[#E8D8A8]">Launching Soon</span>
              </div>
              <DialogTitle className="font-head text-3xl font-black text-white leading-tight">
                {copy.title}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {copy.tagline} Enter your email or phone to be notified when this vertical launches.
              </DialogDescription>
            </DialogHeader>

            <p className="mt-4 text-white/75 font-body leading-relaxed">
              We&apos;re building India&apos;s smartest AI travel platform. <span className="text-[#E8D8A8] font-semibold">Hotels are live.</span> {copy.tagline}
            </p>

            {!ok ? (
              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="coming-soon-form">
                <div>
                  <label className="ss-label text-white/60 block mb-2">Get notified</label>
                  <Input
                    autoFocus
                    data-testid="coming-soon-contact-input"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="you@email.com  or  9876543210"
                    className="bg-white/5 border-white/15 text-white placeholder:text-white/40 h-12 rounded-xl focus:border-[#C8A34D]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending || !contact.trim()}
                  data-testid="coming-soon-notify-btn"
                  className="ss-btn ss-btn-primary w-full h-12 rounded-xl"
                >
                  {sending ? "Saving..." : (<><Bell className="w-4 h-4 mr-2" /> Notify me</>)}
                </Button>
                {running > 0 && (
                  <p className="text-xs text-white/50 text-center font-body">
                    Join <span className="text-[#E8D8A8] font-semibold">{running.toLocaleString("en-IN")}</span> others on the {vertical} waitlist
                  </p>
                )}
              </form>
            ) : (
              <div className="mt-6 rounded-xl border border-[#16A34A]/30 bg-[#16A34A]/10 p-5 text-center" data-testid="coming-soon-success">
                <div className="mx-auto w-12 h-12 rounded-full bg-[#16A34A]/20 grid place-items-center mb-3">
                  <Check className="w-6 h-6 text-[#4ADE80]" />
                </div>
                <div className="font-head font-bold text-white text-lg">You&apos;re on the list!</div>
                <p className="text-white/60 text-sm mt-1 font-body">
                  We&apos;ll message you the moment {vertical} goes live on SwaanStay.
                </p>
                <p className="text-xs text-white/40 mt-3 font-body">
                  {running.toLocaleString("en-IN")} travellers waiting with you.
                </p>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 text-xs text-white/40 font-body">
              <Sparkles className="w-3.5 h-3.5 text-[#C8A34D]" />
              Meanwhile, keep negotiating on hotels — the best deals close within 48 hours.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
